import styled from 'styled-components';
import { Booking } from 'swagger/models';
import { useCloseModal } from 'components/remodal/v2/hooks';
import { useUserDetails } from 'auth/mutations/userHooks';
import Button from 'components/ui/Button/Button';
import { dollarsWithCentsWithSymbol } from '../../helpers/currency';
import { mediaMSmMax } from '../../helpers/breakpoints';

type ModalProps = {
  booking: Booking;
  pricing?: any;
};

const PriceBreakdownModal = ({
  booking,
  pricing,
  booking: {
    boat_price,
    owner,
    captain_price,
    owner_insurance_fee,
    owner_listing_fee,
    renter_pricing,
    special_offer_price,
    pay_captain_at_dock,
    state,
  },
}: ModalProps) => {
  const closeModal = useCloseModal();
  const { userId } = useUserDetails();

  const priceAdjustment = pricing ? pricing?.special_offer_price : special_offer_price;
  const boatPrice = pricing
    ? pricing.boat_price + (pricing?.special_offer_price || 0)
    : boat_price + special_offer_price;
  const boatPriceWithoutPrice = pricing ? pricing.boat_price : boat_price;
  const total = pricing ? pricing.pre_tax_and_fees_booking_total || 0 : renter_pricing.inquiry_total_price;
  const insurance = pricing ? pricing?.owner_insurance_fee : owner_insurance_fee;
  const serviceFee = pricing ? pricing?.owner_listing_fee : owner_listing_fee;
  const payout = pricing ? pricing?.earnings_including_captain : booking?.owner_payout;
  const addonsPrice = pricing ? pricing?.owner_add_on_total : booking?.owner_add_on_total;

  return (
    <div>
      <Heading>Price breakdown</Heading>
      <Breakdown>
        <Name>Boat price</Name>
        <Price>{dollarsWithCentsWithSymbol(boatPrice)}</Price>
        {addonsPrice !== 0 && (
          <>
            <Name>Add-ons</Name>
            <Price>{dollarsWithCentsWithSymbol(addonsPrice)}</Price>
          </>
        )}
        {captain_price > 0 && (
          <>
            <Name>Captain price</Name>
            <Price>{dollarsWithCentsWithSymbol(captain_price)}</Price>
          </>
        )}
        <HorizontalLine />
        <Name>Total (excl. fees)</Name>
        <Price>
          <b>{dollarsWithCentsWithSymbol(total)}</b>
        </Price>
      </Breakdown>
      <Padding />
      <Heading>Payout breakdown</Heading>
      <Breakdown>
        <Name>Boat price</Name>
        <Price>{dollarsWithCentsWithSymbol(boatPriceWithoutPrice)}</Price>
        {addonsPrice !== 0 && (
          <>
            <Name>Add-ons</Name>
            <Price>{dollarsWithCentsWithSymbol(addonsPrice)}</Price>
          </>
        )}
        {captain_price > 0 && !pay_captain_at_dock && (
          <>
            <Name>Captain price</Name>
            <Price>{dollarsWithCentsWithSymbol(captain_price)}</Price>
          </>
        )}
        {priceAdjustment !== 0 && (
          <>
            <Name>Price adjustment</Name>
            <Price>{dollarsWithCentsWithSymbol(priceAdjustment)}</Price>
          </>
        )}

        <Name>Listing fee</Name>
        <Price>-{dollarsWithCentsWithSymbol(serviceFee)}</Price>

        {!!insurance && insurance > 0 && (
          <>
            <Name>Insurance{state === 'initialized' || state === 'pre_approved' ? ' (est)' : ''}</Name>
            <Price>-{dollarsWithCentsWithSymbol(insurance)}</Price>
          </>
        )}

        <HorizontalLine />
        <Name>{owner.id === userId ? 'You earn' : 'Total (excl. fees)'}</Name>
        <GreenPrice>
          <b>{dollarsWithCentsWithSymbol(payout)}</b>
        </GreenPrice>
      </Breakdown>
      <Button fullWidth outline variant="owner" text="Close" onClick={closeModal} />
    </div>
  );
};

const Padding = styled.div`
  height: 20px;
`;

const Breakdown = styled.dl`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-row-gap: 20px;
  padding-bottom: 40px;

  ${mediaMSmMax} {
    grid-row-gap: 10px;
    padding-bottom: 20px;
  }
`;

const Name = styled.dt`
  font-weight: 500;
`;

const Price = styled.dd`
  margin-left: auto;
  font-weight: 600;
  font-size: 14px;
`;

const GreenPrice = styled(Price)`
  color: #2a8500;
  font-size: 16px;
`;

const HorizontalLine = styled.hr`
  grid-column: 1 / span 2;
  border-top: 1px solid #dbdfe5;
  width: 100%;
  margin: 3px 0;

  ${mediaMSmMax} {
    margin-bottom: 5px;
    margin-top: 0;
  }
`;

const Heading = styled.div`
  font-weight: 600;
  font-size: 20px;
  padding-bottom: 20px;
`;

export default PriceBreakdownModal;
